import React, { useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import { TdsTypography } from "@tds/react";
import { computeEmwURL } from "../utils/fetch";
import { StyledAutocomplete } from "./McdAutocomplete";
import { useHza } from "../utils/hzaSync/HzaContext";
import { WORKER_API_ENDPOINT } from "../constants";

export const NO_RESOURCE_ALLOCATED = "No resource allocated";

const defaultStyle = {
  backgroundColor: "white",
  borderRadius: "3px",
  ".MuiInputBase-sizeSmall": {
    padding: 0,
  },
};

const Loading = () => {
  return (
    <div className="flex items-center">
      <TdsTypography fontSize={6} classes="ml-xxs">
        Loading ...
      </TdsTypography>
    </div>
  );
};

export default function EmwAutocomplete(props) {
  const {
    data,
    placeHolder,
    additionalCSS,
    variant,
    onAutocompleteChange,
    disabled,
    defaultValue,
    inputId,
    calculateAutocompleteSugestion,
    calculateAutocompleteLabel,
    autoFocus,
    onInputChange,
    orgId,
    isSearchHeader,
  } = props;
  const [filterAutocomplete, setFilterAutocomplete] = useState("noFilter");
  const [throttledSearch, setThrottledSearch] = useState();
  const [suggestions, setSuggestions] = useState([
    { label: "", value: "", object: {} },
  ]);
  const [val, setVal] = useState({});
  const [shouldSearch, setShouldSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { handleRequest, requestStatus } = useHza();
  let selectedValue = null;

  useEffect(() => {
    const { isLoading: hzaLoading, data, name } = requestStatus;

    if (name !== "HZA_REQUEST_AUTOCOMPLETE") {
      return;
    }

    setIsLoading(hzaLoading);

    if (hzaLoading) {
      return;
    }

    if (data && Array.isArray(data)) {
      const newObject = data.map((value) => ({
        label: calculateAutocompleteSugestion(value),
        value: value.id,
        object: value,
      }));
      setSuggestions(newObject);
    }
  }, [requestStatus]);

  useEffect(() => {
    if (typeof data === "undefined") {
      if (typeof throttledSearch === "undefined") {
        setThrottledSearch(startTimeoutForSearch(filterAutocomplete));
      } else {
        clearTimeout(throttledSearch);
        setThrottledSearch(startTimeoutForSearch(filterAutocomplete));
      }
    } else if (filterAutocomplete === "noFilter") {
      const selectedObject = data.filter(function (element) {
        return element.value === (defaultValue ? defaultValue.value : null);
      });
      if (selectedObject.length > 0) {
        setVal({ label: selectedObject[0].label });
      }
    }
  }, [filterAutocomplete]);

  const startTimeoutForSearch = (filterAutocompleteP) => {
    return setTimeout(function () {
      if (shouldSearch && !disabled) {
        let urlParams = "?val=" + filterAutocompleteP;

        if (orgId) {
          urlParams += "&orgId=" + orgId;
        }

        fetchData(filterAutocompleteP, urlParams);
      } else if (
        filterAutocompleteP === "noFilter" &&
        typeof data === "undefined" &&
        selectedValue == null
      ) {
        setVal({ label: defaultValue });
        selectedValue = defaultValue;
      }
    }, 1000);
  };

  const fetchData = (filterAutocompleteP, urlParams) => {
    const api = computeEmwURL(WORKER_API_ENDPOINT, null, null, null, urlParams);
    const params = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    };

    handleRequest(api, params, "HZA_REQUEST_AUTOCOMPLETE");

    if (
      filterAutocompleteP === "noFilter" &&
      typeof data === "undefined" &&
      selectedValue == null
    ) {
      setVal({ label: defaultValue });
      selectedValue = defaultValue;
    }
  };

  const handleChange = (event, value) => {
    setVal({ label: value ? calculateAutocompleteLabel(value) : "" });
    if (typeof onAutocompleteChange === "function") {
      if (value?.object) {
        value.label = calculateAutocompleteLabel(value);
      }
      onAutocompleteChange(value, inputId);
    }
  };

  const handleInputChange = (event, value, reason) => {
    if (reason === "reset" && isSearchHeader) {
      setVal("");
    }
    if (value !== "") {
      if (event) {
        setShouldSearch(true);
      }
      setFilterAutocomplete(value);
    }

    if (onInputChange) {
      onInputChange(value);
    }
  };

  const filterMethod = (item, state) => {
    if (item.hasOwnProperty("label") && Array.isArray(item.label)) {
      let found = false;
      const searchParts = state.inputValue.split(" ");

      for (let i = 0; i <= searchParts.length; i++) {
        if (searchParts[i]) {
          const searchPartLowercase = searchParts[i].toLowerCase();
          const isPartInLabel = item.label.some((label) =>
            label.toLowerCase().includes(searchPartLowercase),
          );
          if (isPartInLabel) {
            found = true;
            break;
          }
        }
      }

      return found;
    }
  };

  const handleFilterOptions = (options, state) => {
    return options.filter((item) => filterMethod(item, state));
  };

  const getOptionLabel = (option) => {
    return option.label ? option.label + "" : "";
  };

  const renderOption = (props, option) => {
    if (!option.label) {
      return "";
    }
    if (Array.isArray(option.label)) {
      return (
        <li {...props} className={`flex-col items-start ${props.className}`}>
          {option.label.map((label, index) => {
            return <div key={index + label}>{label}</div>;
          })}
        </li>
      );
    }

    return "";
  };

  const isOptionEqualToValue = (option, value) => {
    const val1 = value ? value.label : "";
    return (
      typeof val1 === "undefined" ||
      option.label === "" ||
      option.label === val1
    );
  };

  const renderInput = (params) => {
    return (
      <TextField
        error={
          !params.inputProps.value && placeHolder === NO_RESOURCE_ALLOCATED
        }
        {...params}
        autoFocus={autoFocus}
        variant={variant}
        placeholder={placeHolder}
      />
    );
  };

  if (disabled) {
    return <TdsTypography component="caption-1">{placeHolder}</TdsTypography>;
  }

  return (
    <StyledAutocomplete
      size="small"
      autoHighlight
      loading={isLoading}
      loadingText={<Loading />}
      value={val}
      id={inputId}
      sx={{ ...defaultStyle, ...additionalCSS }}
      disabled={typeof disabled !== "undefined" ? disabled : false}
      defaultValue={typeof data !== "undefined" ? defaultValue : null}
      disableClearable={true}
      options={data ? data : suggestions}
      onChange={handleChange}
      onInputChange={handleInputChange}
      filterOptions={handleFilterOptions}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      isOptionEqualToValue={isOptionEqualToValue}
      renderInput={renderInput}
    />
  );
}
